import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges, OnDestroy,
  Output, SimpleChanges
} from '@angular/core';
import { TranslationDirective } from "./translation.directive";
import { TextHelper } from '../../../helper/text.helper';

@Directive({
  selector: '[fiCustomSelect]',
  standalone: false
})
export class CustomSelectDirective implements AfterViewInit, OnChanges, OnDestroy {
  private selectedText: HTMLElement;
  private selectedTextSearch: HTMLInputElement;
  private selectStat = false;
  private selectedValues = new Set<string>();
  private shouldCloseOnClick = true;

  @Input() displaySelectionValue: string | number | (string | number)[];
  @Input() data: any[] | null;
  @Input() selectedData: any[] | null;
  @Input() dataValueKey: string;
  @Input() translate: boolean;
  @Input() dataNameKey: string;
  @Input() dataImageKey: string;
  @Input() placeHolder: string;
  @Input() isMultiSelect: boolean;
  @Input() selectedOptionsText: string;
  @Input() allSelectedOptionsText: string;
  @Input() readonly : boolean;
  @Output() value = new EventEmitter<string | string[]>();

  set selectOpened(toOpen: boolean) {
    if (toOpen && !this.selectStat) {
      this.separateSelectedAndAllOptions();
    }

    if (!this.isMultiSelect && this.selectedTextSearch) {
      if (toOpen) {
        this.selectedTextSearch.value = this.selectedText.innerText;
        this.selectedTextSearch.classList.add('active');

        if (!this.selectStat) {
          this.filterOptions('');
        }
      } else {
        this.selectedTextSearch.classList.remove('active');
      }
    }

    this.selectStat = toOpen;
    if (toOpen) {
      this.el.nativeElement.classList.add('opened');
      setTimeout(() => {
        let elementToFocus: HTMLElement;

        this.el.nativeElement.querySelectorAll('.option').forEach((option: HTMLElement) => {
          if (this.selectedText?.innerText === option.innerText) {
            elementToFocus = option;
          }
        });

        elementToFocus?.focus();
      }, 100);
    } else {
      this.el.nativeElement.classList.remove('opened');
    }

    this.updateSearchInputValue();
  }

  constructor(
    private el: ElementRef
  ) {

  }

  refreshReadOnly(): void {
    if (this.el.nativeElement instanceof HTMLElement) {
      this.readonly ? this.el.nativeElement.classList.add('readonly') : this.el.nativeElement.classList.remove('readonly');
    }
  }

  ngOnDestroy(): void {
  }

  ngAfterViewInit(dataChanges = false): void {
    if (this.data === null || dataChanges) {
      this.addOptions();
      this.selectedText = this.el.nativeElement.querySelector('.selected-value');
      this.selectedTextSearch = this.el.nativeElement.querySelector('.search');
      this.addListeners();
      this.applyPassedSelectedValues();
      this.refreshReadOnly();
    }

    const container = (this.el.nativeElement as HTMLElement).querySelector('.options') as HTMLElement;

    container.onmouseenter =
      container.onmouseleave = e => {
        if (this.isMultiSelect && e.type === 'mouseenter') {
          this.shouldCloseOnClick = false
        } else {
          this.shouldCloseOnClick = true;
        }
      }
  }

  applyPassedSelectedValues(): void {
    if (this.selectedData instanceof Array && this.isMultiSelect && this.dataValueKey) {
      this.selectedValues = new Set(this.selectedData.map(item => `${item[this.dataValueKey]}`));
      this.el?.nativeElement?.querySelectorAll('.option').forEach(opt => {
        if (this.selectedValues.has(opt.dataset.value)) {
          opt.querySelector('.checkbox').classList.add('checked');
        } else {
          opt.querySelector('.checkbox').classList.remove('checked');
        }
      })
      this.updateSearchInputValue();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.data === null) {
      return;
    }

    this.refreshReadOnly();

    this.shouldCloseOnClick = this.shouldCloseOnClick || !this.isMultiSelect;

    if (changes.selectedData?.currentValue instanceof Array) {
      this.applyPassedSelectedValues();
    }

    if (this.data?.length) {
      this.ngAfterViewInit(true);
      if (this.displaySelectionValue && this.dataNameKey && this.dataValueKey) {
        if (this.displaySelectionValue === 'Filters') {
          this.displaySelectionValue = this.placeHolder;
        }

        const selection = this.data.find(item => item[this.dataValueKey] === this.displaySelectionValue);

        if (selection && selection[this.dataNameKey]) {
          this.setText(selection[this.dataNameKey]);
        }
      } else {
        this.setText(this.placeHolder ? this.placeHolder : '');
      }
    }
  }

  @HostListener('click')
  onClick(): void {
    if (!this.selectStat) {
      this.selectOpened = true;

      setTimeout(() => {
        this.selectedTextSearch?.focus();
      }, 300);

      setTimeout(() => {
        const f = () => {
          document.addEventListener('click', (ev) => {
            const outside = !(ev.target instanceof HTMLElement) || !(this.el?.nativeElement as HTMLElement)?.contains(ev.target);

            if (this.shouldCloseOnClick || outside) {
              this.selectOpened = false;
            } else {
              f();
            }
          }, { once: true });
        };
        f();
      }, 0);
    } else {
      if (this.shouldCloseOnClick) {
        this.selectOpened = false;
      }
    }
  }

  private separateSelectedAndAllOptions(): void {
    if (!this.selectedOptionsText || !this.allSelectedOptionsText || !this.isMultiSelect || !this.el?.nativeElement) {
      return;
    }

    const selectedOptionsTitleEl = document.createElement('li');
    selectedOptionsTitleEl.innerText = this.selectedOptionsText;
    const nonSelectedOptionsTitleEl = document.createElement('li');
    nonSelectedOptionsTitleEl.innerText = this.allSelectedOptionsText;
    selectedOptionsTitleEl.className = nonSelectedOptionsTitleEl.className = 'group';

    const selectedOptions: HTMLElement[] = [];
    const allOptions: HTMLElement[] = [];

    this.el.nativeElement.querySelectorAll('.option').forEach(option => {
      if (option.dataset.cloned === 'true') {
        return;
      }

      if (option.querySelector('.checked')) {
        const clonedOption = option.cloneNode(true);
        clonedOption.dataset.cloned = 'true';
        clonedOption.onclick = option.onclick;
        selectedOptions.push(clonedOption);
      }

      allOptions.push(option);
    });

    const optionsContainer = this.el.nativeElement.querySelector('.options');

    if (selectedOptions.length && allOptions.length) {
      optionsContainer.replaceChildren(selectedOptionsTitleEl, ...selectedOptions, nonSelectedOptionsTitleEl, ...allOptions);
    } else if (selectedOptions.length) {
      optionsContainer.replaceChildren(selectedOptionsTitleEl, ...selectedOptions);
    } else if (allOptions.length) {
      optionsContainer.replaceChildren(nonSelectedOptionsTitleEl, ...allOptions);
    } else {
      optionsContainer.replaceChildren();
    }
  }

  private addListeners(): void {
    this.el.nativeElement.querySelectorAll('.select-input-custom').forEach((input: HTMLInputElement) => {
      input.onkeyup = () => {
        input.dataset.value = input.value;

        this.el.nativeElement.querySelectorAll('.option').forEach((option: HTMLElement) => {
          if (!this.selectStat) {
            input.click();
          }
        })

        this.filterOptions(input.value);
      };

      input.onblur = () => {
        this.updateSearchInputValue();
      }
    });

    this.el.nativeElement.querySelectorAll('.option').forEach(item => {
      item.onclick = (event) => {
        const option = event.target.closest('[data-value]') as HTMLElement;
        this.optionClick(option);
      };

      item.onkeydown = (event: KeyboardEvent) => {
        const option = event.currentTarget as HTMLElement;
        if (event.key === 'ArrowUp' && option.previousElementSibling) {
          (option.previousElementSibling as HTMLElement).focus();
        } else if (event.key === 'ArrowDown' && option.nextElementSibling) {
          (option.nextElementSibling as HTMLElement).focus();
        } else if (event.key === 'Enter' || event.code === 'Space') {
          event.preventDefault();
          this.optionClick(option);
          this.focusToTheNextInput(event);
        } else if (event.key === 'Tab') {
          this.focusToTheNextInput(event);
        }
      };

      item.onfocus = (event: FocusEvent) => {
        if (event.relatedTarget && !(event.relatedTarget as HTMLElement).classList.contains('option')) {
          this.selectOpened = true;
        }
      };
    });

    if (this.selectedTextSearch) {
      this.selectedTextSearch.onblur = this.selectedTextSearch.onclick = (e) => {
        e.stopPropagation();
      };

      this.selectedTextSearch.onkeyup = () => this.filterOptions(this.selectedTextSearch.value);
    }
  }

  private filterOptions(value: string): void {
    const search = value.trim().toLowerCase();

    this.el.nativeElement.querySelectorAll('.option').forEach((option: HTMLElement) => {
      option.style.display = option.innerText.toLowerCase().includes(search) ? '' : 'none';
    })
  }

  private focusToTheNextInput(event: Event): void {
    const elements = Array.from(document.querySelectorAll('input:not(.search), textarea, .selected-value'));

    let passedCurrentElement = false;

    for (const el of elements) {
      if (passedCurrentElement) {
        event.preventDefault();

        setTimeout(() => {
          //this.selectOpened = false;
        }, 20);

        setTimeout(() => {
          if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
            el.focus();
          } else {
            (el as HTMLElement).click();
          }
        }, 200);
        return;
      }

      if (this.selectedText === el) {
        passedCurrentElement = true;
      }
    }
  }

  private optionClick(option: HTMLElement): void {
    const value = option.dataset.value;
    const text = option.innerText.trim();

    if (this.isMultiSelect) {
      if (this.selectedValues.has(value)) {
        this.selectedValues.delete(value);
        this.findOptionsByValue(option.parentElement, value).forEach(el => {
          el.querySelector('.checkbox').classList.remove('checked');
        });
      } else {
        this.selectedValues.add(value);
        this.findOptionsByValue(option.parentElement, value).forEach(el => {
          el.querySelector('.checkbox').classList.add('checked');
        });
      }

      this.value.emit([...this.selectedValues]);
    } else {
      this.setText(text);
      this.value.emit(value);
    }
  }

  private addOptions(): void {
    if (!this.data?.length || !this.dataNameKey || !this.dataNameKey) {
      return;
    }

    const options = this.data.map(item => {
      const text = (this.translate ? TranslationDirective.data[item[this.dataNameKey]] : item[this.dataNameKey]);
      const option = document.createElement('li');

      option.className = 'option';
      option.dataset.value = item[this.dataValueKey];

      if (!this.readonly) {
        option.setAttribute('tabindex', '0');
      }

      if (!this.isMultiSelect) {
        option.textContent = TextHelper.decodeHtml(text);

        return option;
      }

      const hasImage = !!this.dataImageKey && item[this.dataImageKey] !== 'none';
      option.classList.add('no-hilight-on-focus', hasImage ? 'unlimited-height' : 'limited-height');

      const textContainer = document.createElement('div');
      textContainer.className = 'option-text-container';

      const checkbox = document.createElement('div');
      checkbox.className = this.selectedValues.has(item[this.dataValueKey]) ? 'checkbox checked' : 'checkbox';
      textContainer.append(checkbox);

      if (hasImage) {
        const imageContainer = document.createElement('div');
        imageContainer.className = 'image-cnt';

        const image = document.createElement('img');
        image.alt = 'image';
        image.setAttribute('src', item[this.dataImageKey]);
        imageContainer.append(image);
        textContainer.append(imageContainer);
      }

      const textElement = document.createElement('div');
      textElement.className = 'text';
      textElement.textContent = TextHelper.decodeHtml(text);
      textContainer.append(textElement);

      option.append(textContainer);

      return option;
    });

    this.el.nativeElement.querySelector('.options').replaceChildren(...options);
  }

  /**
   * Options carry arbitrary data values, so an interpolated
   * `[data-value="..."]` selector is both injectable and a syntax error for any
   * value containing a quote. Compare the parsed dataset value instead.
   */
  private findOptionsByValue(container: HTMLElement, value: string): HTMLElement[] {
    return [...container.querySelectorAll('.option')]
      .filter((el: HTMLElement) => el.dataset.value === value) as HTMLElement[];
  }

  private setText(text: string): void {
    this.el.nativeElement.querySelector('.selected-value').classList.remove('placeholder');
    this.selectedText.innerText = text;

    if (this.selectedTextSearch) {
      this.selectedTextSearch.value = text;
    }
  }

  private updateSearchInputValue(): void {
    const input = this.el?.nativeElement.querySelector('.select-input-custom') as HTMLInputElement;

    if (input && this.isMultiSelect) {
      if (this.selectStat || input === document.activeElement) {
        input.value = input.dataset.value ?? '';
        input.style.overflow = '';
        input.style.textOverflow = '';
        input.style.marginRight = '';
      } else {
        input.style.overflow = 'hidden';
        input.style.textOverflow = 'ellipsis';
        input.style.marginRight = '10px';
        input.value = [...this.el.nativeElement.querySelectorAll('.option:not([data-cloned="true"])')].filter((o: HTMLElement) => o.querySelector('.checked')).map((o: HTMLElement) => o.innerText).join(', ');
      }
    }
  }
}
